import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import GlobalStyles from '@mui/material/GlobalStyles';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import RestartAltIcon from '@mui/icons-material/RestartAltOutlined';
import FullscreenIcon from '@mui/icons-material/FullscreenOutlined';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExitOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import PrintDisabledIcon from '@mui/icons-material/PrintDisabledOutlined';
import SensorsIcon from '@mui/icons-material/SensorsOutlined';
import SensorsOffIcon from '@mui/icons-material/SensorsOffOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import favicon from '@/images/fav.png';
import { apiClient } from '@/api/axiosClient';
import SearchableSelect from '@/components/common/SearchableSelect';
import LabelObjectRenderer from '@/components/labelDesigner/LabelObjectRenderer';
import { PERMISSIONS } from '@/types/auth';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useLiveWeight, printRawViaBridge, reconnectBridgeSerial } from '@/hooks/useLiveWeight';
import { buildTsplLabel } from '@/lib/tspl';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { LabelTemplate } from '@/types/labelTemplate';
import type { HardwareStationDto } from '@/types/weightProduction';
import type {
  ConfirmWeightEntryRequest,
  PrintWeightProductionEntryRequest,
  ValidateWeightRequest,
  ValidateWeightResultDto,
  WeightProductionEntryDto,
  WeightProductionOrderDto,
} from '@/types/weightProduction';

const PRINT_SCALE = 3.7795; // px per mm at 96dpi, used only by the browser-print fallback

// The one "this is live and healthy" colour on the header bar — used by the scale and printer status
// icons and by whichever capture mode is active, so the operator reads them as one family. Kept as a
// literal rather than a palette token because it has to sit on the dark gradient, not on paper.
const CONNECTED_GREEN = '#7CFFB2';

function buildSampleData(entry: WeightProductionEntryDto): Record<string, string> {
  return {
    BarcodeNo: entry.barcodeNo,
    OrderNumber: entry.orderNumber ?? '',
    MaterialCode: entry.materialCode,
    MaterialName: entry.materialName,
    StandardWeight: String(entry.standardWeight),
    MinWeight: String(entry.minWeight),
    MaxWeight: String(entry.maxWeight),
    ActualWeight: String(entry.actualWeight),
    WeightUom: entry.weightUom,
    ToleranceStatus: entry.toleranceStatus,
    WarehouseName: entry.warehouseName,
    HardwareStationName: entry.hardwareStationName ?? '',
    OperatorName: entry.operatorUserName,
    BatchNo: entry.batchNo ?? '',
    ExpiryDate: entry.expiryDate ? new Date(entry.expiryDate).toLocaleDateString() : '',
    ProductionDate: entry.createdDate,
    PrintDate: new Date().toLocaleDateString(),
    PrintTime: new Date().toLocaleTimeString(),
  };
}

// After a label is printed the kiosk clears itself and waits for the NEXT weighing — the operator
// replaces the material and the next label only comes once that unit also reaches tolerance. This
// is how long the printed barcode stays on screen before that automatic reset.
const AUTO_RESET_DELAY_MS = 2500;

/** One compact figure in the spec rail. */
function StatTile({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <Box sx={{ px: 1.5, py: 1.25, borderRadius: 2, bgcolor: 'action.hover', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: accent, lineHeight: 1.3 }} noWrap>
        {value}
      </Typography>
    </Box>
  );
}

export default function WeightProductionKioskPage() {
  const { t } = useTranslation('weightProduction');
  const queryClient = useQueryClient();
  const { agentAvailable, scaleConnected, scaleError, status: bridgeStatus, reading } = useLiveWeight();

  const { data: orders = [] } = useQuery({
    queryKey: ['weight-production-orders'],
    queryFn: async () => (await apiClient.get<WeightProductionOrderDto[]>('/weight-production-orders')).data,
  });
  // isReadyToProduce, not just the status: a BOM plan is released long before it can be weighed —
  // its inputs must be issued from stock first, or a finished-goods label would exist for material
  // that never left inventory. Direct plans qualify the moment they are released.
  const releasedOrders = useMemo(
    () => orders.filter((o) => o.isReadyToProduce
      && (o.status === 'Released' || o.status === 'InProduction' || o.status === 'PartiallyCompleted')),
    [orders],
  );

  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const weightMaterials = (materialsPage?.items ?? []).filter((m) => m.weightBasedProductionEnabled);

  const { data: hardwareStations = [] } = useQuery({
    queryKey: ['hardware-stations'],
    queryFn: async () => (await apiClient.get<HardwareStationDto[]>('/hardware-stations')).data,
  });
  const { data: templates = [] } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get<LabelTemplate[]>('/label-templates')).data,
  });
  // Recent weighings, for the reprint strip under the stage. Refetched after every capture so the
  // label that just came out is the top row.
  const { data: recentEntries = [] } = useQuery({
    queryKey: ['weight-production-entries'],
    queryFn: async () => (await apiClient.get<WeightProductionEntryDto[]>('/weight-production-entries')).data,
  });

  const [orderId, setOrderId] = useState<string>('direct');
  const selectedOrder = releasedOrders.find((o) => o.id === orderId);
  // Filtered on isMet, not on remainingQty: a weight line can be finished with a few grams still
  // showing, because that leftover is smaller than any label anyone could produce. Filtering on the
  // number would leave unworkable lines in the picker forever.
  const orderLines = selectedOrder?.lines.filter((l) => !l.isMet) ?? [];

  const [orderLineId, setOrderLineId] = useState('');
  const selectedLine = orderLines.find((l) => l.id === orderLineId);

  const [materialId, setMaterialId] = useState('');
  const effectiveMaterialId = selectedOrder ? selectedLine?.materialId ?? '' : materialId;
  const material = weightMaterials.find((m) => m.id === effectiveMaterialId);
  const autoPrintTemplate = templates.find((tpl) => tpl.id === material?.weightProductionLabelTemplateId && tpl.status === 'Active');

  // Batch is whatever the operator typed on the Production Plan line — the same value this weighing
  // will actually be posted against (see WeightProductionEntryService.ConfirmEntryAsync). No plan
  // line (Direct/no-plan) means no batch to preview here; the server may still fall back to a
  // date-based one if the item has general batch tracking on, but that isn't knowable client-side.
  const previewBatchNo = selectedLine?.batchNo ?? null;
  // Expiry is a pure preview until an entry is actually confirmed: today + the item's Shelf Life
  // Days, mirroring the server's own calculation exactly (mandatory on the Item Master once Weigh &
  // Label is enabled — see MaterialFormDialog's superRefine).
  const previewExpiryDate = useMemo(() => {
    if (!material?.shelfLifeRequired || !material.shelfLifeDays) return null;
    const d = new Date();
    d.setDate(d.getDate() + material.shelfLifeDays);
    return d;
  }, [material?.shelfLifeRequired, material?.shelfLifeDays]);

  // The station is never picked by hand: the agent running on this PC already knows which station
  // it is (Station:Code in its appsettings), so the master record is resolved by matching that code.
  // An operator choosing the wrong station from a dropdown would silently mis-attribute every
  // weighing on the line.
  const resolvedStation = hardwareStations.find((s) => s.code === bridgeStatus?.station.code) ?? null;
  // The Hardware Configuration master is the authority for what a station is called — the agent's
  // own Name is only a fallback for before/if the master is resolved. When the agent reports a code
  // that matches no master, that is called out rather than quietly showing the agent's local name,
  // because in that state weighings are stamped with NO station at all.
  const stationDisplayName = resolvedStation?.name ?? bridgeStatus?.station.name ?? null;
  const stationUnmatched = agentAvailable && !resolvedStation;

  // Destination is never re-asked either — the plan states its warehouse (and the plan line its
  // location); Direct mode falls back to the item's own defaults from the Item Master.
  const derivedWarehouseId = selectedOrder?.warehouseId ?? material?.defaultWarehouseId ?? '';
  const derivedWarehouseName = selectedOrder?.warehouseName ?? material?.defaultWarehouseName ?? null;
  // The plan's own Zone (chosen once at plan level — see WeightProductionOrdersPage) wins over the
  // line's own location or the material's default: a plan says where its OWN output lands,
  // regardless of what any single line or item happens to default to elsewhere.
  const derivedLocationId = selectedOrder?.locationId ?? selectedLine?.locationId ?? material?.defaultLocationId ?? null;
  const derivedLocationCode = selectedOrder?.locationCode ?? selectedLine?.locationCode ?? material?.defaultLocationCode ?? null;

  const [weightInput, setWeightInput] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  // Some sites want a human to sign off every label even with a scale attached, others want the line
  // to run untouched. This is that choice, and it survives reloads per station rather than resetting
  // to Auto every time the browser restarts (an operator would not notice it had).
  const [captureMode, setCaptureMode] = useState<'Auto' | 'Manual'>(
    () => (localStorage.getItem('ims.weightKioskCaptureMode') === 'Manual' ? 'Manual' : 'Auto'),
  );
  const changeCaptureMode = (mode: 'Auto' | 'Manual') => {
    setCaptureMode(mode);
    localStorage.setItem('ims.weightKioskCaptureMode', mode);
    resetCapture();
  };

  // Kiosk (fullscreen) mode: the same screen promoted to an overlay covering the app shell, so a
  // shop-floor terminal shows nothing but the weighing task. Native fullscreen is requested too, but
  // the overlay does the hiding — fullscreen can be refused and the mode still has to work.
  const [kioskMode, setKioskMode] = useState(false);
  const enterKioskMode = () => {
    setKioskMode(true);
    void document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const exitKioskMode = () => {
    setKioskMode(false);
    if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => {});
  };
  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) setKioskMode(false);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // The preview is scaled to whatever width the rail actually gives it, rather than a fixed px/mm.
  // A hard-coded scale clipped wide templates (a 100×75mm label overflowed the card and lost its
  // right-hand side), and the whole point of the panel is to show the label in full.
  const previewBoxRef = useRef<HTMLDivElement | null>(null);
  const [previewBox, setPreviewBox] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = previewBoxRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setPreviewBox({ width: rect.width, height: rect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [autoPrintTemplate?.id, kioskMode]);

  const [validation, setValidation] = useState<ValidateWeightResultDto | null>(null);
  const [lastEntry, setLastEntry] = useState<WeightProductionEntryDto | null>(null);
  const [printJob, setPrintJob] = useState<{ entry: WeightProductionEntryDto; template: LabelTemplate } | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  // A label the Bridge couldn't print, held so the operator can CHOOSE to fall back to the browser's
  // print dialog. The dialog is never opened for them — that choice is the whole point.
  const [pendingBrowserPrint, setPendingBrowserPrint] = useState<{ entry: WeightProductionEntryDto; template: LabelTemplate } | null>(null);

  const printViaBrowser = () => {
    if (!pendingBrowserPrint) return;
    setPrintJob(pendingBrowserPrint);
    setPendingBrowserPrint(null);
    window.setTimeout(() => window.print(), 150);
  };

  const resetCapture = () => {
    setWeightInput('');
    setValidation(null);
    setLastEntry(null);
    setOverrideReason('');
    setPrintJob(null);
    setPrintError(null);
    setPendingBrowserPrint(null);
  };

  const validateMutation = useMutation({
    mutationFn: async () => {
      const request: ValidateWeightRequest = {
        materialId: effectiveMaterialId,
        actualWeight: Number(weightInput),
        weightProductionOrderLineId: selectedOrder ? orderLineId || null : null,
      };
      return (await apiClient.post<ValidateWeightResultDto>('/weight-production-entries/validate', request)).data;
    },
    onSuccess: setValidation,
  });

  const autoPrint = async (entry: WeightProductionEntryDto) => {
    if (material && !material.autoPrintLabel) {
      setPrintError(t('kiosk.autoPrintDisabled'));
      return;
    }
    if (!autoPrintTemplate) {
      setPrintError(t('kiosk.noAutoPrintTemplate'));
      return;
    }
    setPrintJob({ entry, template: autoPrintTemplate });
    try {
      // Recorded server-side first — a label that physically came out but isn't in the print history
      // would leave a reprint with no record of the original.
      const request: PrintWeightProductionEntryRequest = { weightProductionEntryId: entry.id, labelTemplateId: autoPrintTemplate.id };
      await apiClient.post('/weight-production-printing', request);
    } catch (error) {
      setPrintError(error instanceof Error ? error.message : t('kiosk.printFailed'));
      return;
    }

    // Same rule as reprint: no dialog ever appears on its own. Without a Bridge the weighing is
    // still captured and stocked — only the label is missing, and that is said plainly.
    if (!agentAvailable) {
      setPendingBrowserPrint({ entry, template: autoPrintTemplate });
      setPrintError(t('kiosk.bridgeUnavailableForPrint'));
      return;
    }

    try {
      // Straight to the print head through the agent: no dialog, and barcodes are encoded by the
      // printer rather than resampled from a browser bitmap.
      const { commands, skippedImages } = buildTsplLabel(autoPrintTemplate, buildSampleData(entry));
      await printRawViaBridge(commands, undefined, `Weight label ${entry.barcodeNo}`);
      if (skippedImages > 0) setPrintError(t('kiosk.imagesSkipped', { count: skippedImages }));
    } catch (error) {
      setPendingBrowserPrint({ entry, template: autoPrintTemplate });
      setPrintError(error instanceof Error ? error.message : t('kiosk.printFailed'));
    }
  };

  // Takes the weight explicitly rather than reading `weightInput` state: live capture decides on a
  // reading and fires in the same tick, where setWeightInput() hasn't applied yet.
  const confirmMutation = useMutation({
    mutationFn: async (actualWeight: number) => {
      const request: ConfirmWeightEntryRequest = {
        materialId: effectiveMaterialId,
        actualWeight,
        warehouseId: derivedWarehouseId,
        locationId: derivedLocationId,
        weightProductionOrderLineId: selectedOrder ? orderLineId || null : null,
        hardwareStationId: resolvedStation?.id ?? null,
        overProductionOverrideReason: overrideReason || null,
        captureMode,
      };
      return (await apiClient.post<WeightProductionEntryDto>('/weight-production-entries/confirm', request)).data;
    },
    onSuccess: async (entry) => {
      setLastEntry(entry);
      queryClient.invalidateQueries({ queryKey: ['weight-production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['weight-production-entries'] });
      await autoPrint(entry);
      window.setTimeout(resetCapture, AUTO_RESET_DELAY_MS);
    },
  });

  const canValidate = Boolean(effectiveMaterialId) && Boolean(weightInput) && Number(weightInput) > 0;
  const canConfirm = canValidate && Boolean(derivedWarehouseId) && validation?.inTolerance === true;

  // ---- Live capture --------------------------------------------------------------------------
  // Two conditions must hold together: the reading is settled (the scale's ST/US token, or the
  // agent's identical-readings fallback), and it sits inside the item's min/max. Capture fires once
  // and disarms; it re-arms only after the pan returns below minimum — i.e. the unit was physically
  // removed. Without that, a unit left on the scale would print a label per reading.
  const armedRef = useRef(true);
  const liveWeight = reading?.weight ?? null;
  const liveStable = reading?.stable === true;
  const minWeight = material?.minWeight ?? null;
  const maxWeight = material?.maxWeight ?? null;
  const liveInTolerance = liveWeight !== null && minWeight !== null && maxWeight !== null && liveWeight >= minWeight && liveWeight <= maxWeight;
  const liveMode = agentAvailable && scaleConnected;
  const liveCaptureReady = liveMode && Boolean(effectiveMaterialId) && Boolean(derivedWarehouseId);
  // Manual mode keeps the live readout and the tolerance check — the only thing it changes is that
  // a person, not the kiosk, decides when a label is produced.
  const autoCaptureReady = liveCaptureReady && captureMode === 'Auto';
  // In Manual mode the operator confirms the scale's own reading; a typed value overrides it for the
  // rare case where the item can't sit on the pan.
  const manualCaptureWeight = weightInput ? Number(weightInput) : liveWeight;
  const manualCaptureInTolerance =
    manualCaptureWeight !== null && minWeight !== null && maxWeight !== null
    && manualCaptureWeight >= minWeight && manualCaptureWeight <= maxWeight;

  useEffect(() => {
    if (!autoCaptureReady || liveWeight === null || minWeight === null) return;
    if (liveWeight < minWeight) {
      armedRef.current = true;
      return;
    }
    if (!armedRef.current || !liveStable || !liveInTolerance) return;
    if (confirmMutation.isPending || lastEntry) return;

    armedRef.current = false;
    setWeightInput(String(liveWeight));
    confirmMutation.mutate(liveWeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveWeight, liveStable, liveInTolerance, autoCaptureReady, minWeight]);

  // A completed line disappears from the picker (orderLines already filters remainingQty > 0) and a
  // fully-produced plan leaves the released list once the server rolls its status to Completed. Both
  // would otherwise leave the kiosk pointing at a selection that no longer exists, so the selection
  // is cleared to follow: the item first, then the plan when its last line is done.
  useEffect(() => {
    if (orderLineId && selectedOrder && !orderLines.some((l) => l.id === orderLineId)) {
      setOrderLineId('');
      resetCapture();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderLineId, orderLines.length, selectedOrder?.id]);

  useEffect(() => {
    if (orderId !== 'direct' && !releasedOrders.some((o) => o.id === orderId)) {
      setOrderId('direct');
      setOrderLineId('');
      setMaterialId('');
      resetCapture();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, releasedOrders.length]);

  /** The label assigned to whichever item a recent row is for — a reprint must use that entry's own
   * item template, not whatever item happens to be selected on screen right now. */
  const templateForEntry = (entry: WeightProductionEntryDto): LabelTemplate | undefined => {
    const entryMaterial = (materialsPage?.items ?? []).find((m) => m.id === entry.materialId);
    return templates.find((tpl) => tpl.id === entryMaterial?.weightProductionLabelTemplateId && tpl.status === 'Active');
  };

  const [reconnecting, setReconnecting] = useState(false);
  const [reprintingId, setReprintingId] = useState<string | null>(null);
  const [reprintError, setReprintError] = useState<string | null>(null);

  // Straight back out of the printer, no dialog and no questions. The server requires a reason on
  // every reprint, so the kiosk supplies a fixed one naming where it came from — the audit trail
  // still distinguishes reprints from originals without stopping the line for a prompt.
  const reprintMutation = useMutation({
    mutationFn: async (entry: WeightProductionEntryDto) => {
      const template = templateForEntry(entry);
      if (!template) throw new Error(t('kiosk.noTemplateForEntry'));

      const request: PrintWeightProductionEntryRequest = {
        weightProductionEntryId: entry.id,
        labelTemplateId: template.id,
        isReprint: true,
        reprintReason: t('kiosk.reprintReasonKiosk'),
      };
      await apiClient.post('/weight-production-printing', request);

      // Never falls back to window.print(): a print dialog appearing on a shop-floor kiosk is a
      // trap — it blocks the screen until someone dismisses it, and an operator holding a part is
      // not looking for a mouse. If the Bridge can't take the job, that is reported as an error and
      // the operator can retry deliberately (see pendingBrowserPrint for the explicit escape hatch).
      if (!agentAvailable) {
        setPendingBrowserPrint({ entry, template });
        throw new Error(t('kiosk.bridgeUnavailableForPrint'));
      }
      const { commands } = buildTsplLabel(template, buildSampleData(entry));
      await printRawViaBridge(commands, undefined, `Weight label ${entry.barcodeNo}`);
    },
    onMutate: (entry) => {
      setReprintError(null);
      setReprintingId(entry.id);
    },
    onError: (error) => setReprintError(error instanceof Error ? error.message : t('kiosk.printFailed')),
    onSettled: () => setReprintingId(null),
  });

  // Enter = produce this label. An operator on a kiosk keeps a hand on the keypad, types the weight
  // (or lets the scale supply it) and expects Enter to finish the job — reaching for a mouse per
  // label is the slow part of the loop. Listens on the document rather than the field so it works
  // whether or not the weight box has focus, and e.key === 'Enter' covers the numpad key too.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.repeat) return;
      if (lastEntry || confirmMutation.isPending) return;

      // Never hijack Enter while a picker is open or in multi-line text — there it means "choose
      // this option"/"new line", and stealing it would make the selects unusable.
      const target = event.target as HTMLElement | null;
      if (target?.closest('[role="listbox"], [role="combobox"], [role="option"], textarea')) return;
      if (document.querySelector('[role="listbox"]')) return;

      if (liveMode && captureMode === 'Manual') {
        if (manualCaptureWeight !== null && manualCaptureInTolerance) {
          event.preventDefault();
          confirmMutation.mutate(manualCaptureWeight);
        }
        return;
      }

      // Manual-entry station (no scale): first Enter validates, second confirms — so a weight is
      // still checked against tolerance before a label is produced.
      if (!liveMode) {
        if (canConfirm) {
          event.preventDefault();
          confirmMutation.mutate(Number(weightInput));
        } else if (canValidate && !validateMutation.isPending) {
          event.preventDefault();
          validateMutation.mutate();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMode, captureMode, manualCaptureWeight, manualCaptureInTolerance, canConfirm, canValidate, weightInput, lastEntry, confirmMutation.isPending, validateMutation.isPending]);

  const previewSampleData = useMemo<Record<string, string>>(() => {
    if (lastEntry) return buildSampleData(lastEntry);
    if (!material) return {};
    const now = new Date();
    return {
      BarcodeNo: t('kiosk.previewBarcode'),
      OrderNumber: selectedOrder?.orderNumber ?? '',
      MaterialCode: material.code,
      MaterialName: material.name,
      StandardWeight: String(material.standardWeight ?? ''),
      MinWeight: String(material.minWeight ?? ''),
      MaxWeight: String(material.maxWeight ?? ''),
      ActualWeight: liveWeight !== null ? liveWeight.toFixed(3) : weightInput || '—',
      WeightUom: material.weightUom ?? '',
      ToleranceStatus: liveInTolerance ? 'Valid' : '',
      WarehouseName: derivedWarehouseName ?? '',
      HardwareStationName: resolvedStation?.name ?? '',
      OperatorName: '',
      BatchNo: previewBatchNo ?? '',
      ExpiryDate: previewExpiryDate ? previewExpiryDate.toLocaleDateString() : '',
      ProductionDate: now.toISOString(),
      PrintDate: now.toLocaleDateString(),
      PrintTime: now.toLocaleTimeString(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEntry, material, selectedOrder, liveWeight, liveInTolerance, weightInput, derivedWarehouseName, resolvedStation, previewBatchNo, previewExpiryDate]);

  // Tolerance band. The in-tolerance window is drawn as the middle third so a near-miss is visually
  // obvious even when min/max are only grams apart (1.99–2.01 would otherwise be a hairline).
  const displayWeight = liveMode ? liveWeight : weightInput ? Number(weightInput) : null;
  const band = useMemo(() => {
    if (minWeight === null || maxWeight === null) return null;
    const width = Math.max(maxWeight - minWeight, 0.001);
    return { low: minWeight - width, high: maxWeight + width };
  }, [minWeight, maxWeight]);
  const markerPercent = band && displayWeight !== null
    ? Math.min(100, Math.max(0, ((displayWeight - band.low) / (band.high - band.low)) * 100))
    : null;

  const inTolerance = liveMode ? liveInTolerance : validation?.inTolerance === true;
  const heroAccent = lastEntry ? 'success.main' : displayWeight === null ? 'text.disabled' : inTolerance ? 'success.main' : 'warning.main';

  const producedPercent = selectedLine ? Math.min(100, (selectedLine.producedQty / Math.max(1, selectedLine.plannedQty)) * 100) : null;
  // A Quantity line counts labels, not kilograms — showing "18.025 / 20 KG" against a piece-count
  // plan is exactly the confusion this basis exists to remove.
  const planUnit = selectedLine?.planBasis === 'Quantity' ? t('kiosk.unitPcs') : material?.weightUom ?? '';
  // px per mm that fits the label inside the space it's been given. In Kiosk Mode the panel has a
  // bounded height (the whole screen must fit without scrolling), so the smaller of the two fits
  // wins; in normal page flow the height is content-driven, so width alone governs.
  const previewScale = (() => {
    if (!autoPrintTemplate || previewBox.width <= 0) return 0;
    const byWidth = previewBox.width / autoPrintTemplate.widthMm;
    if (!kioskMode || previewBox.height <= 0) return byWidth;
    return Math.min(byWidth, previewBox.height / autoPrintTemplate.heightMm);
  })();

  return (
    <Box
      sx={
        kioskMode
          ? {
              // Fit-to-screen: the shop floor should never scroll to reach the reprint list or the
              // preview. 100dvh (not vh) so a mobile/tablet browser's collapsing toolbar can't push
              // content under the fold.
              position: 'fixed', inset: 0, zIndex: (theme) => theme.zIndex.drawer + 2,
              bgcolor: 'background.default', height: '100dvh', overflow: 'hidden',
              display: 'flex', flexDirection: 'column', gap: 1.5, p: { xs: 1.5, md: 2 },
            }
          : undefined
      }
    >
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#kiosk-label-print-area, #kiosk-label-print-area *': { visibility: 'visible' },
            '#kiosk-label-print-area': { position: 'absolute', top: 0, left: 0, margin: 0 },
          },
          '@page': { size: printJob ? `${printJob.template.widthMm}mm ${printJob.template.heightMm}mm` : 'auto', margin: 0 },
          '@keyframes kioskPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.35 },
          },
          '@keyframes kioskPop': {
            '0%': { transform: 'scale(0.94)', opacity: 0 },
            '100%': { transform: 'scale(1)', opacity: 1 },
          },
        }}
      />

      {/* Header strip — station identity on the left, connection truth on the right. */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flexShrink: 0,
          mb: kioskMode ? 0 : 2, px: 2.5, py: kioskMode ? 1.25 : 1.75, borderRadius: 3,
          color: 'common.white',
          background: (theme) => `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${alpha(theme.palette.secondary.main, 0.9)} 100%)`,
          boxShadow: (theme) => `0 10px 28px ${alpha(theme.palette.primary.main, 0.35)}`,
        }}
      >
        <Box
          sx={{
            width: 42, height: 42, flexShrink: 0, borderRadius: '50%', bgcolor: 'common.white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.common.black, 0.25)}`,
          }}
        >
          <Box component="img" src={favicon} alt="Inventra" sx={{ width: 30, height: 30, borderRadius: '50%' }} />
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 800, letterSpacing: 0.3, lineHeight: 1.2 }} noWrap>
            {t('kiosk.title')}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
            {stationDisplayName ?? t('kiosk.noStation')}
            {derivedWarehouseName ? ` · ${derivedWarehouseName}${derivedLocationCode ? ` · ${derivedLocationCode}` : ''}` : ''}
          </Typography>
        </Box>
        <Chip
          icon={liveMode ? <SensorsIcon /> : <SensorsOffIcon />}
          label={liveMode ? t('kiosk.chipLive', { port: bridgeStatus?.serial.port ?? '' }) : agentAvailable ? t('kiosk.chipScaleOffline') : t('kiosk.chipManual')}
          sx={{
            fontWeight: 700, color: 'common.white',
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.25),
            '& .MuiChip-icon': { color: liveMode ? CONNECTED_GREEN : '#FFD27A' },
          }}
        />
        {agentAvailable && bridgeStatus && (
          // Real spooler state, so a missing/offline/jammed printer is visible BEFORE an operator
          // weighs 20 units and wonders where the labels went. Hover gives the exact reason.
          <Tooltip
            title={
              bridgeStatus.printer.problem
                ?? `${bridgeStatus.printer.status}${bridgeStatus.printer.queuedJobs > 0 ? ` · ${t('kiosk.printerQueued', { count: bridgeStatus.printer.queuedJobs })}` : ''}`
            }
          >
            <Chip
              icon={bridgeStatus.printer.online ? <PrintIcon /> : <PrintDisabledIcon />}
              label={bridgeStatus.printer.exists ? bridgeStatus.printer.name : t('kiosk.printerMissing')}
              sx={{
                fontWeight: 600, color: 'common.white',
                bgcolor: (theme) => alpha(bridgeStatus.printer.online ? theme.palette.common.black : theme.palette.error.main, bridgeStatus.printer.online ? 0.25 : 0.75),
                '& .MuiChip-icon': { color: bridgeStatus.printer.online ? CONNECTED_GREEN : '#FFD9D9' },
              }}
            />
          </Tooltip>
        )}
        {/* Auto vs Manual capture. Only meaningful with a scale attached — without one every capture
            is manual by definition, so the choice is hidden rather than shown as a no-op. */}
        {liveMode && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={captureMode}
            onChange={(_, value) => value && changeCaptureMode(value as 'Auto' | 'Manual')}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.25), borderRadius: 2,
              '& .MuiToggleButton-root': { color: 'common.white', border: 'none', fontWeight: 700, px: 1.5, py: 0.5 },
              // Active mode in the same green as the live scale/printer indicators, with dark text
              // on it for contrast — the mint is too light to read white against.
              '& .Mui-selected, & .Mui-selected:hover': {
                bgcolor: `${CONNECTED_GREEN} !important`,
                color: (theme) => `${theme.palette.primary.dark} !important`,
                fontWeight: 800,
              },
            }}
          >
            <ToggleButton value="Auto">{t('kiosk.modeAuto')}</ToggleButton>
            <ToggleButton value="Manual">{t('kiosk.modeManual')}</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Button
          variant="contained"
          color="inherit"
          startIcon={kioskMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
          onClick={kioskMode ? exitKioskMode : enterKioskMode}
          sx={{ bgcolor: 'common.white', color: 'primary.main', fontWeight: 700, '&:hover': { bgcolor: 'common.white' } }}
        >
          {kioskMode ? t('kiosk.exitKioskMode') : t('kiosk.kioskMode')}
        </Button>
      </Box>

      {agentAvailable && !scaleConnected && (
        <Alert
          severity="warning"
          sx={{ mb: kioskMode ? 0 : 2, flexShrink: 0 }}
          action={
            <Button
              size="small"
              startIcon={<RestartAltIcon />}
              disabled={reconnecting}
              onClick={async () => {
                setReconnecting(true);
                try {
                  await reconnectBridgeSerial();
                } catch {
                  // The status poll reports the outcome either way — no second error channel needed.
                } finally {
                  window.setTimeout(() => setReconnecting(false), 3000);
                }
              }}
            >
              {reconnecting ? t('kiosk.reconnecting') : t('kiosk.reconnectScale')}
            </Button>
          }
        >
          {bridgeStatus?.serial.released
            ? t('kiosk.scaleReleased', { port: bridgeStatus?.serial.port ?? '' })
            : t('kiosk.scaleOffline', { port: bridgeStatus?.serial.port ?? '', error: scaleError ?? '' })}
        </Alert>
      )}
      {stationUnmatched && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('kiosk.stationUnmatched', { code: bridgeStatus?.station.code ?? '' })}
        </Alert>
      )}

      {/* Two columns built explicitly rather than with the shared grid helpers: Kiosk Mode needs the
          columns to inherit a bounded height so nothing overflows the viewport, which requires
          flex/minHeight control the helpers don't expose. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: kioskMode ? 1.5 : 2,
          ...(kioskMode ? { flex: 1, minHeight: 0 } : {}),
        }}
      >
        {/* ---------------- Weighing stage ---------------- */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: kioskMode ? 1.5 : 2, minHeight: 0 }}>
          <Card
            variant="outlined"
            sx={{
              borderWidth: 2, borderRadius: 4, overflow: 'hidden',
              borderColor: lastEntry ? 'success.main' : inTolerance && displayWeight !== null ? 'success.main' : 'divider',
              background: (theme) => `radial-gradient(1200px 320px at 50% -10%, ${alpha(theme.palette.primary.main, 0.10)}, transparent 70%)`,
              ...(kioskMode ? { flex: 1, minHeight: 0, display: 'flex' } : {}),
            }}
          >
            <CardContent
              sx={{
                p: { xs: 2, md: kioskMode ? 2 : 4 },
                ...(kioskMode ? { flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' } : {}),
              }}
            >
              <Stack alignItems="center" spacing={kioskMode ? 1 : 1.5} sx={kioskMode ? { width: '100%' } : undefined}>
                {material ? (
                  <Stack alignItems="center" spacing={0.25}>
                    <Typography sx={{ fontWeight: 800, fontSize: 22 }}>{material.code}</Typography>
                    <Typography variant="body2" color="text.secondary">{material.name}</Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">{t('kiosk.selectItemFirst')}</Typography>
                )}

                {/* The number the whole screen exists for. */}
                <Typography
                  sx={{
                    // Height-aware as well as width-aware: on a short screen a purely vw-based size
                    // is what pushed the rest of the kiosk past the fold.
                    fontSize: kioskMode ? 'clamp(56px, min(11vw, 17vh), 150px)' : 'clamp(72px, 13vw, 168px)',
                    fontWeight: 800, lineHeight: 0.95,
                    letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums', color: heroAccent,
                    textShadow: (theme) => (displayWeight !== null && inTolerance ? `0 0 42px ${alpha(theme.palette.success.main, 0.35)}` : 'none'),
                    transition: 'color .18s ease',
                  }}
                >
                  {displayWeight === null ? '—' : displayWeight.toFixed(3)}
                  <Typography component="span" sx={{ fontSize: '0.24em', fontWeight: 700, ml: 1.5, color: 'text.secondary', letterSpacing: 0 }}>
                    {material?.weightUom ?? reading?.unit ?? ''}
                  </Typography>
                </Typography>

                {liveMode && (
                  <Chip
                    size="medium"
                    color={liveStable ? 'success' : 'default'}
                    variant={liveStable ? 'filled' : 'outlined'}
                    label={liveStable ? t('kiosk.stable') : t('kiosk.settling')}
                    sx={{ fontWeight: 800, letterSpacing: 1, px: 1, animation: liveStable ? 'none' : 'kioskPulse 1.2s ease-in-out infinite' }}
                  />
                )}

                {/* Tolerance band — under/in/over zones with a marker for the current reading. */}
                {band && (
                  <Box sx={{ width: '100%', maxWidth: 620, pt: 1 }}>
                    <Box
                      sx={{
                        position: 'relative', height: 30, borderRadius: 2, overflow: 'hidden', display: 'flex',
                        border: 1, borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ flex: 1, bgcolor: (theme) => alpha(theme.palette.error.main, 0.18) }} />
                      <Box sx={{ flex: 1, bgcolor: (theme) => alpha(theme.palette.success.main, 0.35) }} />
                      <Box sx={{ flex: 1, bgcolor: (theme) => alpha(theme.palette.error.main, 0.18) }} />
                      {markerPercent !== null && (
                        <Box
                          sx={{
                            position: 'absolute', top: -3, bottom: -3, left: `${markerPercent}%`, width: 4, ml: '-2px',
                            bgcolor: inTolerance ? 'success.main' : 'error.main', borderRadius: 1,
                            transition: 'left .15s ease-out',
                            boxShadow: (theme) => `0 0 12px ${alpha(inTolerance ? theme.palette.success.main : theme.palette.error.main, 0.8)}`,
                          }}
                        />
                      )}
                    </Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                      <Typography variant="caption" color="text.secondary">{t('kiosk.minimum')} {minWeight}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{t('kiosk.targetWeight')} {material?.standardWeight ?? '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('kiosk.maximum')} {maxWeight}</Typography>
                    </Stack>
                  </Box>
                )}

                {/* Captured — the barcode that just came out of the printer. */}
                {lastEntry && (
                  <Box sx={{ textAlign: 'center', animation: 'kioskPop .25s ease-out' }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <CheckCircleIcon color="success" />
                      <Typography sx={{ fontWeight: 800, fontSize: 20, fontVariantNumeric: 'tabular-nums' }}>{lastEntry.barcodeNo}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{t('kiosk.replaceMaterialHint')}</Typography>
                  </Box>
                )}
                {printError && (
                  <Alert
                    severity="warning"
                    sx={{ width: '100%' }}
                    action={pendingBrowserPrint && (
                      <Button size="small" onClick={printViaBrowser}>{t('kiosk.printViaBrowser')}</Button>
                    )}
                  >
                    {printError}
                  </Alert>
                )}

                {/* Manual fallback only — live mode drives itself. */}
                <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_EXECUTE}>
                  {/* Scale attached, but this site wants a person on every label. The live reading is
                      what gets captured; the field below only overrides it when the item genuinely
                      can't be weighed on the pan. */}
                  {liveMode && captureMode === 'Manual' && !lastEntry && (
                    <Stack spacing={1.25} alignItems="center" sx={{ pt: 0.5, width: '100%' }}>
                      {validation?.wouldExceedPlan && (
                        <>
                          <Alert severity="warning" sx={{ width: '100%' }}>{t('kiosk.overPlanWarning')}</Alert>
                          <TextField label={t('kiosk.overrideReason')} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} fullWidth size="small" />
                        </>
                      )}
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <TextField
                          type="number"
                          size="small"
                          label={t('kiosk.overrideWeight')}
                          value={weightInput}
                          placeholder={liveWeight !== null ? liveWeight.toFixed(3) : ''}
                          onChange={(e) => { setWeightInput(e.target.value); setValidation(null); }}
                          slotProps={{ htmlInput: { step: '0.001', style: { textAlign: 'center', fontWeight: 700 } } }}
                          sx={{ width: 170 }}
                        />
                        <Button
                          variant="contained"
                          size="large"
                          disabled={manualCaptureWeight === null || !manualCaptureInTolerance || confirmMutation.isPending}
                          onClick={() => manualCaptureWeight !== null && confirmMutation.mutate(manualCaptureWeight)}
                        >
                          {t('kiosk.confirmAndPrint')}
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {manualCaptureInTolerance ? t('kiosk.manualReadyEnter') : t('kiosk.manualOutOfTolerance')}
                      </Typography>
                    </Stack>
                  )}

                  {!liveMode && !lastEntry && (
                    <Stack spacing={1.5} alignItems="center" sx={{ pt: 1 }}>
                      <TextField
                        type="number"
                        label={t('kiosk.enterWeight')}
                        value={weightInput}
                        onChange={(e) => { setWeightInput(e.target.value); setValidation(null); }}
                        slotProps={{ htmlInput: { step: '0.001', style: { textAlign: 'center', fontSize: 30, fontWeight: 700 } } }}
                        sx={{ width: 260 }}
                      />
                      {validation?.wouldExceedPlan && (
                        <>
                          <Alert severity="warning">{t('kiosk.overPlanWarning')}</Alert>
                          <TextField label={t('kiosk.overrideReason')} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} fullWidth size="small" />
                        </>
                      )}
                      <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" size="large" disabled={!canValidate || validateMutation.isPending} onClick={() => validateMutation.mutate()}>
                          {t('kiosk.validate')}
                        </Button>
                        <Button variant="contained" size="large" disabled={!canConfirm || confirmMutation.isPending} onClick={() => confirmMutation.mutate(Number(weightInput))}>
                          {t('kiosk.confirmAndPrint')}
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                  {autoCaptureReady && !lastEntry && (
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                      {armedRef.current ? t('kiosk.awaitingMaterial') : t('kiosk.removeMaterial')}
                    </Typography>
                  )}
                  {lastEntry && (
                    <Button variant="text" startIcon={<RestartAltIcon />} onClick={resetCapture}>{t('kiosk.reset')}</Button>
                  )}
                </IfPermitted>
              </Stack>
            </CardContent>
          </Card>

          {/* Recent labels — sits in the space under the stage so reprinting never needs a scroll
              or a trip to another screen. Capped at five rows for exactly that reason. */}
          <Card variant="outlined" sx={{ borderRadius: 3, flexShrink: 0 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {t('kiosk.recentLabels')}
                </Typography>
                <Typography variant="caption" color="text.secondary">{t('kiosk.recentLabelsHint')}</Typography>
              </Stack>

              {reprintError && (
                <Alert
                  severity="warning"
                  sx={{ my: 1 }}
                  action={pendingBrowserPrint && (
                    <Button size="small" onClick={printViaBrowser}>{t('kiosk.printViaBrowser')}</Button>
                  )}
                >
                  {reprintError}
                </Alert>
              )}

              {recentEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{t('kiosk.noRecentLabels')}</Typography>
              ) : (
                <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                  {recentEntries.slice(0, 5).map((entry) => (
                    <Stack
                      key={entry.id}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ py: 0.75, opacity: entry.status === 'Cancelled' ? 0.5 : 1 }}
                    >
                      <Typography sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', minWidth: 140, fontSize: 14 }} noWrap>
                        {entry.barcodeNo}
                      </Typography>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{entry.materialCode}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {`${entry.orderNumber ?? t('kiosk.direct')} · ${new Date(entry.createdDate).toLocaleTimeString()}`}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 90, textAlign: 'right', fontSize: 14 }}>
                        {`${entry.actualWeight} ${entry.weightUom}`}
                      </Typography>
                      <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_EXECUTE}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PrintIcon fontSize="small" />}
                          disabled={reprintingId !== null || entry.status === 'Cancelled'}
                          onClick={() => reprintMutation.mutate(entry)}
                        >
                          {reprintingId === entry.id ? t('kiosk.reprinting') : t('printing.reprint')}
                        </Button>
                      </IfPermitted>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* ---------------- Job rail ---------------- */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: kioskMode ? 1.5 : 2, minHeight: 0 }}>
            <Card variant="outlined" sx={{ borderRadius: 3, flexShrink: 0 }}>
              <CardContent>
                <Stack spacing={2}>
                  <SearchableSelect
                    label={t('kiosk.planOrDirect')} value={orderId} fullWidth
                    onChange={(v) => { setOrderId(v); setOrderLineId(''); setMaterialId(''); resetCapture(); }}
                    options={[
                      { value: 'direct', label: t('kiosk.direct') },
                      ...releasedOrders.map((o) => ({ value: o.id, label: `${o.orderNumber} — ${o.warehouseName}` })),
                    ]}
                  />
                  {selectedOrder ? (
                    <SearchableSelect
                      label={t('kiosk.material')} value={orderLineId} fullWidth
                      onChange={(v) => { setOrderLineId(v); resetCapture(); }}
                      options={orderLines.map((l) => ({
                        value: l.id,
                        label: `${l.materialCode} — ${l.materialName} (${l.remainingQty} ${l.planBasis === 'Quantity' ? t('kiosk.unitPcs') : ''} ${t('kiosk.remaining')})`.replace(/\s+/g, ' '),
                      }))}
                    />
                  ) : (
                    <SearchableSelect
                      label={t('kiosk.material')} value={materialId} fullWidth
                      onChange={(v) => { setMaterialId(v); resetCapture(); }}
                      options={weightMaterials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                    />
                  )}

                  {material && (
                    <Stack direction="row" spacing={1}>
                      <Box sx={{ flex: 1 }}><StatTile label={t('kiosk.minimum')} value={`${material.minWeight ?? '—'}`} /></Box>
                      <Box sx={{ flex: 1 }}><StatTile label={t('kiosk.targetWeight')} value={`${material.standardWeight ?? '—'}`} accent="primary.main" /></Box>
                      <Box sx={{ flex: 1 }}><StatTile label={t('kiosk.maximum')} value={`${material.maxWeight ?? '—'}`} /></Box>
                    </Stack>
                  )}

                  {/* Batch/expiry — whatever this weighing will actually be posted against. Batch
                      comes from the Production Plan line; expiry is today + the item's Shelf Life
                      Days, the same calculation the server makes at confirm time. */}
                  {material && (lastEntry?.batchNo || previewBatchNo || lastEntry?.expiryDate || previewExpiryDate) && (
                    <Stack direction="row" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <StatTile label={t('kiosk.batchNo')} value={lastEntry?.batchNo ?? previewBatchNo ?? t('kiosk.batchAuto')} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <StatTile
                          label={t('kiosk.expiryDate')}
                          value={
                            lastEntry?.expiryDate
                              ? new Date(lastEntry.expiryDate).toLocaleDateString()
                              : previewExpiryDate
                                ? previewExpiryDate.toLocaleDateString()
                                : '—'
                          }
                          accent="warning.main"
                        />
                      </Box>
                    </Stack>
                  )}

                  {selectedLine && (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary">{t('kiosk.planProgress')}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{`${Math.round(producedPercent ?? 0)}%`}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <StatTile label={t('kiosk.plannedQty')} value={`${selectedLine.plannedQty} ${planUnit}`} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <StatTile label={t('kiosk.producedQty')} value={`${selectedLine.producedQty} ${planUnit}`} accent="success.main" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <StatTile label={t('kiosk.remainingQty')} value={`${selectedLine.remainingQty} ${planUnit}`} accent="warning.main" />
                        </Box>
                      </Stack>
                      <LinearProgress variant="determinate" value={producedPercent ?? 0} sx={{ height: 10, borderRadius: 5 }} />
                    </Box>
                  )}

                  {material && !derivedWarehouseId && <Alert severity="warning" sx={{ py: 0.5 }}>{t('kiosk.noWarehouseResolved')}</Alert>}
                </Stack>
              </CardContent>
            </Card>

            {/* Label preview — the same renderer the Label Designer uses, so this is genuinely what
                the template contains, not an approximation of it. */}
            {autoPrintTemplate ? (
              <Card variant="outlined" sx={{ borderRadius: 3, ...(kioskMode ? { flex: 1, minHeight: 0, display: 'flex' } : {}) }}>
                <CardContent
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    ...(kioskMode ? { flex: 1, minHeight: 0, width: '100%', '&:last-child': { pb: 2 } } : {}),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, alignSelf: 'flex-start' }}>
                    {lastEntry ? t('kiosk.labelPrinted') : t('kiosk.labelPreview')}
                  </Typography>
                  {/* Outer box is the measured container; the inner one is the label itself at
                      exactly the scale that fills it, so the whole label is always visible. */}
                  <Box
                    ref={previewBoxRef}
                    sx={{
                      width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                      ...(kioskMode ? { flex: 1, minHeight: 0 } : {}),
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative', bgcolor: '#fff', borderRadius: 1, border: 1, borderColor: 'divider', boxShadow: 2,
                        overflow: 'hidden', flexShrink: 0,
                        width: previewScale > 0 ? autoPrintTemplate.widthMm * previewScale : '100%',
                        height: previewScale > 0 ? autoPrintTemplate.heightMm * previewScale : 0,
                      }}
                    >
                      {previewScale > 0 && autoPrintTemplate.objects.map((obj) => (
                        <LabelObjectRenderer key={obj.id} object={obj} sampleData={previewSampleData} scale={previewScale} />
                      ))}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {`${autoPrintTemplate.name} · ${autoPrintTemplate.widthMm}×${autoPrintTemplate.heightMm} mm`}
                    {agentAvailable ? ` · ${t('kiosk.directPrint', { printer: bridgeStatus?.printer.name ?? '' })}` : ''}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              material && (
                <Alert severity="warning">{t('kiosk.noAutoPrintTemplateHint')}</Alert>
              )
            )}
        </Box>
      </Box>

      {/* Off-screen print area — browser-print fallback only (no agent). */}
      {printJob && (
        <Box
          id="kiosk-label-print-area"
          sx={{
            position: 'fixed', top: '-9999px', left: '-9999px', bgcolor: '#fff',
            width: `${printJob.template.widthMm}mm`, height: `${printJob.template.heightMm}mm`,
          }}
        >
          {printJob.template.objects.map((obj) => (
            <LabelObjectRenderer key={obj.id} object={obj} sampleData={buildSampleData(printJob.entry)} scale={PRINT_SCALE} />
          ))}
        </Box>
      )}
    </Box>
  );
}
